import { useCallback } from "react";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";

export type SchedulerCloserAvailabilityToday =
  | "bookable"
  | "blocked"
  | "not_started";

export type SchedulerCloserDTO = {
  closerId: string;
  name: string;
  email: string;
  acceptsWeb: boolean;
  acceptsSetter: boolean;
  availabilityToday: SchedulerCloserAvailabilityToday;
  bookableFrom: string | null;
  block: { from: string; to: string } | null;
  invalidBlock: string | null;
  inTrial: boolean;
  capacity: number | null;
  usesDefaultCapacity: boolean;
  tierRank: number | null;
  cr: number | null;
  crWonDeals: number | null;
  crCallsDone: number | null;
  calendarSyncEnabled: boolean;
};

export type SchedulerExcludedCloserDTO = {
  closerId: string;
  name: string;
  email: string | null;
  reason: string;
  reasonLabel: string;
};

export type SchedulerClosersDTO = {
  closers: SchedulerCloserDTO[];
  excluded: SchedulerExcludedCloserDTO[];
  capacityDefault: number | null;
  trialDays: number;
  crWindowDays: number;
};

export function useGetSchedulerClosers() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();
  const { withLoading, loading } = useWithLoading<SchedulerClosersDTO>();

  const getClosersCall = useCallback(async () => {
    const { data } = await axiosInstance.get<SchedulerClosersDTO>(
      `/closer-availability/scheduler/closers`,
    );

    if (!Array.isArray(data?.closers) || !Array.isArray(data?.excluded)) {
      console.error("Unexpected scheduler closers response:", data);
      throw new Error("Unexpected response format from HubSpot adapter");
    }

    return data;
  }, [axiosInstance]);

  const getSchedulerClosers = useCallback(async () => {
    return await withLoading(() => getClosersCall());
  }, [withLoading, getClosersCall]);

  return {
    getSchedulerClosers,
    loading,
  };
}
